// @flow
import React from "react";
import cx from "classnames";
import offerStyles from "./OfferSummary.scss";
import sharedStyles from "../shared/Shared.scss";
import Card from "../../Card";
import LabeledContent from "../LabeledContent";
import Status from "../../Status";
import {
  mapBankAdvisorProducts,
  mapCustomerProducts,
  leasingDefaultProduct
} from "../../../views/Details/mappers";
import StaticFieldText from "../../StaticFieldText";

type GuaranteeDecision =
  | "guarantee"
  | "participation"
  | "guarantee_and_participation"
  | "no_approval";

const mapGuaranteeDecision = (decision: GuaranteeDecision) =>
  ({
    guarantee: "Bürgschaft",
    participation: "Betailigung",
    guarantee_and_participation: "Bürgschaft und Beteiligung",
    no_approval: "Keine Bewilligung"
  }: { [GuaranteeDecision]: string })[decision];

type OfferSummaryProps = {
  federalState: string,
  contactPerson: string,
  phoneNumber: string,
  company: string,
  amount: string,
  numberOfBanks: number,
  shouldHideIncludingYours?: boolean,
  status: string,
  decision: GuaranteeDecision,
  guaranteeInterest: string,
  products: Array<string>,
  creator: "bankAdvisor" | "customer" | "leasingAdvisor",
  currencyFormatter: (amount: number) => string
};

const mapProducts = (products, productLabelDictionary) => {
  switch (true) {
    case products.length === 0:
      return null;
    case products.length === 1:
      return productLabelDictionary(products[0]);
    default:
      return (
        <ol className={offerStyles.bankList}>
          {products.map(product => {
            return <li key={product}>{productLabelDictionary(product)}</li>;
          })}
        </ol>
      );
  }
};

const renderProducts = (products, creator) => {
  switch (creator) {
    case "bankAdvisor":
      return mapProducts(products, mapBankAdvisorProducts);
    case "customer":
      return mapProducts(products, mapCustomerProducts);
    case "leasingAdvisor":
      return leasingDefaultProduct;
    default:
      return null;
  }
};

const OfferSummary = ({
  federalState,
  contactPerson,
  phoneNumber,
  company,
  amount,
  numberOfBanks,
  shouldHideIncludingYours = false,
  status,
  decision,
  guaranteeInterest,
  products,
  creator,
  currencyFormatter
}: OfferSummaryProps) => {
  const renderedProducts = renderProducts(products, creator);

  return (
    <Card>
      <div className={sharedStyles.container}>
        <div className={sharedStyles.row}>
          <div className={sharedStyles.col}>
            <LabeledContent label="Bürgschaftsbank">
              <StaticFieldText text={federalState} />
            </LabeledContent>
          </div>
          <div className={cx(sharedStyles.col, sharedStyles.borderRight, sharedStyles.empty)}>
            <div />
          </div>
          <div className={sharedStyles.col}>
            <LabeledContent label="Unternehmen">
              <StaticFieldText text={company} />
            </LabeledContent>
          </div>
          {creator === "customer" ? (
            <div className={cx(sharedStyles.col, sharedStyles.borderRight)}>
              <LabeledContent label="Gewählte Banken">
                {`${numberOfBanks} Stück`}
                {!shouldHideIncludingYours && " inkl. Ihnen"}
              </LabeledContent>
            </div>
          ) : (
            <div className={cx(sharedStyles.col, sharedStyles.borderRight, sharedStyles.empty)}>
              <div />
            </div>
          )}
          <div className={sharedStyles.col}>
            <LabeledContent label="Status">
              <Status site="bank" status={status} />
            </LabeledContent>
          </div>
        </div>
        <div className={sharedStyles.row}>
          <div className={sharedStyles.col}>
            <LabeledContent label="Ansprechpartner">
              <StaticFieldText text={contactPerson} defaultText="In Bearbeitung" />
            </LabeledContent>
          </div>
          <div className={cx(sharedStyles.col, sharedStyles.borderRight)}>
            <LabeledContent label="Telefon">
              <StaticFieldText text={phoneNumber} defaultText="In Bearbeitung" />
            </LabeledContent>
          </div>
          <div className={sharedStyles.col}>
            <LabeledContent label="Produkt">
              <StaticFieldText text={renderedProducts} />
            </LabeledContent>
          </div>
          <div className={cx(sharedStyles.col, sharedStyles.borderRight)}>
            <LabeledContent label="Volumen">
              <StaticFieldText text={amount == null ? "-" : currencyFormatter(amount)} />
            </LabeledContent>
          </div>
          <div className={sharedStyles.col}>
            <LabeledContent label="Besicherung">
              <StaticFieldText
                text={mapGuaranteeDecision(decision)}
                defaultText={guaranteeInterest ? "Keine Besicherung" : "In Bearbeitung"}
              />
            </LabeledContent>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default OfferSummary;
